import {IconType} from '@Components/icon-v2/icon.types';
import type {ReactElement} from 'react';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {assertNever} from '@Utils/assert-never.util';
import type {PlatformPostPreviewProps} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {
  MIN_PAGES_FOR_MULTI_PAGE_LAYOUT,
  PreviewMediaFit,
  PreviewMediaLayout,
} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {FacebookFooter} from './components/facebook-footer/facebook-footer';
import {FacebookOverlay} from './components/facebook-overlay/facebook-overlay';
import {TiktokOverlay} from './components/tiktok-overlay/tiktok-overlay';
import {YoutubeOverlay} from './components/youtube-overlay/youtube-overlay';
import type {VerticalVideoPlatformChrome} from './vertical-video-preview.types';
import {VerticalVideoPreviewPlatform} from './vertical-video-preview.types';
import styles from './vertical-video-preview.module.scss';
import {getCaptionFromPublishingParams} from '@Libraries/social-media-params.library';

export function VerticalVideoPreview({account, platformMediaType, publishingParams, numberOfPages, renderMedia}: PlatformPostPreviewProps): ReactElement {
  const caption = getCaptionFromPublishingParams(publishingParams, account.id, platformMediaType);

  const hasMultiplePages = numberOfPages >= MIN_PAGES_FOR_MULTI_PAGE_LAYOUT;

  const getVerticalVideoPreviewPlatform = (): VerticalVideoPreviewPlatform => {
    switch (account.type) {
      case SocialPlatform.YOUTUBE:
        return VerticalVideoPreviewPlatform.YOUTUBE;
      case SocialPlatform.TIKTOK:
        return VerticalVideoPreviewPlatform.TIKTOK;
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_GROUP:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.INSTAGRAM:
      case SocialPlatform.TWITTER:
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PAGE:
      case SocialPlatform.LINKEDIN_PROFILE:
      case SocialPlatform.PINTEREST:
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      case SocialPlatform.THREADS:
        return VerticalVideoPreviewPlatform.FACEBOOK;
      default:
        return assertNever(account.type);
    }
  };

  const getVerticalVideoPlatformChrome = (): VerticalVideoPlatformChrome => {
    const platform = getVerticalVideoPreviewPlatform();

    switch (platform) {
      case VerticalVideoPreviewPlatform.FACEBOOK:
        return {
          footer: <FacebookFooter />,
          mediaFrameClassName: styles.verticalVideoMediaFrameAboveFooter,
          overlay: <FacebookOverlay account={account} caption={caption} />,
        };
      case VerticalVideoPreviewPlatform.YOUTUBE:
        return {
          footer: null,
          mediaFrameClassName: styles.verticalVideoMediaFrameFull,
          overlay: <YoutubeOverlay account={account} caption={caption} />,
        };
      case VerticalVideoPreviewPlatform.TIKTOK:
        return {
          footer: null,
          mediaFrameClassName: styles.verticalVideoMediaFrameFull,
          overlay: <TiktokOverlay account={account} caption={caption} />,
        };
      default:
        return assertNever(platform);
    }
  };

  const platformChrome = getVerticalVideoPlatformChrome();

  const getMedia = (): ReactElement => {
    if (hasMultiplePages) {
      return renderMedia({
        layout: PreviewMediaLayout.CAROUSEL,
        fit: PreviewMediaFit.COVER,
        frameClassName: platformChrome.mediaFrameClassName,
        carouselClassName: styles.verticalVideoCarousel,
        carouselButtonContainerClassName: styles.verticalVideoCarouselArrows,
        areCarouselArrowsAlwaysVisible: true,
        carouselCounterClassName: '_hidden',
        carouselButtonType: IconType.DARK,
        carouselIndicatorClassName: styles.verticalVideoCarouselIndicators,
      });
    }

    return renderMedia({
      layout: PreviewMediaLayout.SINGLE,
      fit: PreviewMediaFit.COVER,
      frameClassName: platformChrome.mediaFrameClassName,
    });
  };

  return (
    <div className={styles.verticalVideoPreview}>
      {getMedia()}
      {platformChrome.footer}
      <div className={`${styles.verticalVideoOverlayLayer} ${hasMultiplePages ? styles.verticalVideoOverlayLayerAboveCarouselControls : ''}`}>{platformChrome.overlay}</div>
    </div>
  );
}
